import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  getSchumannCurrent,
  getSchumannHistory,
  getCosmicCurrent,
  getCosmicHistory,
  getCosmicAlerts,
  getDailyBrief,
  listJournal,
  createJournal,
  deleteJournal,
} from "@/lib/api";
import LiveDot from "@/components/earthpulse/LiveDot";
import SchumannPanel from "@/components/earthpulse/SchumannPanel";
import CosmicPanel from "@/components/earthpulse/CosmicPanel";
import DailyBrief from "@/components/earthpulse/DailyBrief";
import AlertsFeed from "@/components/earthpulse/AlertsFeed";
import SkyEventsToWatch from "@/components/earthpulse/SkyEventsToWatch";
import CalendarSync from "@/components/earthpulse/CalendarSync";
import HistoryChart from "@/components/earthpulse/HistoryChart";
import JournalSection from "@/components/earthpulse/JournalSection";
import BirthChartTeaser from "@/components/earthpulse/BirthChartTeaser";
import IntroReel from "@/components/earthpulse/IntroReel"; // eslint-disable-line no-unused-vars

import MoonPanel from "@/components/earthpulse/MoonPanel";
import BiorhythmPanel from "@/components/earthpulse/BiorhythmPanel";
import BaselineNote from "@/components/earthpulse/BaselineNote";
import MeditationLibrary from "@/components/earthpulse/MeditationLibrary";
import BiorhythmBanner from "@/components/earthpulse/BiorhythmBanner";
import MobileTabBar from "@/components/earthpulse/MobileTabBar";
import CosmicScoreDashboard from "@/components/earthpulse/CosmicScoreDashboard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Sparkles, Sun, Moon as MoonIcon, Waves, BookOpen, Sparkle, PenLine } from "lucide-react";
import SubscribedBadge from "@/components/earthpulse/SubscribedBadge";
import NextSkyEventChip from "@/components/earthpulse/NextSkyEventChip";
import DayOfEventBanner from "@/components/earthpulse/DayOfEventBanner";
import useSubscription from "@/hooks/useSubscription";

export default function Dashboard() {
  const qc = useQueryClient();
  const [historyDays, setHistoryDays] = useState(7);
  const [activeTab, setActiveTab] = useState("today");
  const { active: subActive } = useSubscription();

  const schumann = useQuery({ queryKey: ["schumann-current"], queryFn: getSchumannCurrent, refetchInterval: 60_000 });
  const cosmic = useQuery({ queryKey: ["cosmic-current"], queryFn: getCosmicCurrent, refetchInterval: 60_000 });
  const schumannHist = useQuery({ queryKey: ["schumann-hist", historyDays], queryFn: () => getSchumannHistory(historyDays) });
  const cosmicHist = useQuery({ queryKey: ["cosmic-hist", historyDays], queryFn: () => getCosmicHistory(historyDays) });
  const alerts = useQuery({ queryKey: ["alerts"], queryFn: getCosmicAlerts, refetchInterval: 5 * 60_000 });
  const brief = useQuery({ queryKey: ["brief"], queryFn: getDailyBrief, staleTime: 6 * 60 * 60_000 });
  const journal = useQuery({ queryKey: ["journal"], queryFn: listJournal });

  const addEntry = useMutation({
    mutationFn: createJournal,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["journal"] });
      qc.invalidateQueries({ queryKey: ["brief"] });
      toast.success("Journal entry saved", { description: "The moment is captured with today's field." });
    },
    onError: () => toast.error("Could not save journal entry"),
  });

  const removeEntry = useMutation({
    mutationFn: deleteJournal,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["journal"] });
      qc.invalidateQueries({ queryKey: ["brief"] });
      toast.success("Entry removed");
    },
  });

  const criticalAlerts = useMemo(
    () => (alerts.data?.alerts || []).filter((a) => a.severity !== "info").slice(0, 3),
    [alerts.data],
  );

  useEffect(() => {
    if (schumann.data?.status === "storm") {
      toast("The field is stormy today", {
        description: `Activity ${schumann.data.activity_index}/100 — a good day to rest and ground.`,
      });
    }
  }, [schumann.data?.status, schumann.data?.activity_index]);

  const now = new Date();
  const dateStr = now.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen relative z-10" data-testid="dashboard-root">
      {/* Header */}
      <header
        className="sticky top-0 z-20 border-b border-[color:var(--gold)]/15 backdrop-blur-xl"
        style={{ background: "rgba(10, 17, 40, 0.75)" }}
        data-testid="dashboard-header"
      >
        <div className="mx-auto max-w-[1500px] px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-4 group" data-testid="dashboard-home-link">
            <img
              src="/logo.svg"
              alt="Earth Pulse logo"
              className="w-11 h-11 rounded-full group-hover:scale-105"
              style={{ transition: "transform 200ms ease" }}
            />
            <div>
              <div className="label-overline text-[color:var(--gold)]/80">EARTH · PULSE</div>
              <div className="font-serif-display text-white text-2xl leading-none tracking-tight mt-1">
                daily cosmic guidance
              </div>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <span className="font-serif-italic text-[color:var(--parchment-soft)] text-base">{dateStr}</span>
            <SubscribedBadge />
            {subActive ? (
              <Link
                to="/subscription"
                data-testid="nav-manage-sub"
                className="px-5 py-2 rounded-full border border-[color:var(--gold)]/60 text-[color:var(--parchment)] font-sans-body text-sm hover:bg-[color:var(--gold)]/10 hover:border-[color:var(--gold)] flex items-center gap-2"
              >
                Manage membership
              </Link>
            ) : (
              <Link
                to="/subscribe"
                data-testid="nav-subscribe"
                className="px-5 py-2 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-sans-body text-sm flex items-center gap-2"
              >
                Unlimited readings · £8.88 / month
              </Link>
            )}
            <div className="flex items-center gap-2">
              <LiveDot />
              <span className="label-overline">Live</span>
            </div>
          </div>
        </div>
      </header>

      {/* Sticky biorhythm banner — only renders if DOB in localStorage */}
      <BiorhythmBanner />

      <main className="mx-auto max-w-[1500px] px-6 md:px-12 py-10 md:py-14 pb-24 md:pb-16">
        {/* Social hero: welcome copy full width (video removed — posters showcase on landing) */}
        <section className="mb-14 stagger-in" data-testid="section-intro">
          <div className="label-overline">Welcome</div>
          <h1 className="mt-3 font-serif-display text-[color:var(--parchment)] text-4xl md:text-6xl leading-[1.03] tracking-tight">
            Are you <span className="font-serif-italic text-[color:var(--gold)]">feeling</span> it too?
          </h1>
          <p className="mt-5 text-[color:var(--parchment-soft)] text-base md:text-lg max-w-2xl font-sans-body leading-relaxed">
            Earth&apos;s electromagnetic field pulses, the sun stirs, and something in the air shifts —
            often before we can name it. Earth · Pulse gives you today&apos;s cosmic weather in one place,
            with a warm, literary daily briefing and a personal cosmic birth chart when you&apos;re ready.
          </p>
        </section>

        {/* Non-subscribers get a tease of the next big sky event to nudge signups */}
        <NextSkyEventChip />

        {/* Subscribers get a soft banner on the day of a synced sky event */}
        <DayOfEventBanner />

        {/* Critical alert banner */}
        {criticalAlerts.length > 0 && (
          <div
            data-testid="critical-alert-banner"
            className="mb-10 rounded-lg border border-[color:var(--accent-warning)]/40 p-5 stagger-in"
            style={{ background: "rgba(226, 114, 91, 0.06)" }}
          >
            <div className="flex items-start gap-3">
              <div className="w-2 h-2 mt-2 rounded-full bg-[color:var(--accent-warning)] animate-pulse" />
              <div>
                <div className="label-overline text-[color:var(--accent-warning)]">
                  Cosmic alert · {criticalAlerts[0].severity}
                </div>
                <div className="mt-1 text-[color:var(--parchment)] font-serif-display tracking-tight text-xl">
                  {criticalAlerts[0].title}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabbed navigation — orderly sections so the page isn't a scroll marathon */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div
            className="sticky top-[68px] z-20 -mx-6 md:-mx-12 px-6 md:px-12 py-3 mb-8 backdrop-blur-md"
            style={{ background: "rgba(5, 9, 23, 0.85)", borderBottom: "1px solid rgba(212, 162, 76, 0.15)" }}
          >
            <TabsList
              className="w-full flex md:flex-wrap md:justify-start gap-2 md:gap-3 bg-transparent p-0 h-auto overflow-x-auto no-scrollbar snap-x snap-mandatory"
              data-testid="almanac-tabs"
            >
            <TabsTrigger
              value="today"
              className="flex-shrink-0 snap-start rounded-full border border-[color:var(--gold)]/25 px-4 md:px-5 py-2 md:py-2.5 text-sm md:text-base text-[color:var(--parchment-soft)] data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--bg-base)] data-[state=active]:border-transparent hover:text-[color:var(--parchment)] transition font-sans-body"
              data-testid="tab-today"
            >
              <PenLine size={14} className="mr-2" />Today &amp; Journal
            </TabsTrigger>
            <TabsTrigger
              value="cosmic"
              className="flex-shrink-0 snap-start rounded-full border border-[color:var(--gold)]/25 px-4 md:px-5 py-2 md:py-2.5 text-sm md:text-base text-[color:var(--parchment-soft)] data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--bg-base)] data-[state=active]:border-transparent hover:text-[color:var(--parchment)] transition font-sans-body"
              data-testid="tab-cosmic"
            >
              <Sun size={14} className="mr-2" />Cosmic weather
            </TabsTrigger>
            <TabsTrigger
              value="schumann"
              className="flex-shrink-0 snap-start rounded-full border border-[color:var(--gold)]/25 px-4 md:px-5 py-2 md:py-2.5 text-sm md:text-base text-[color:var(--parchment-soft)] data-[state=active]:bg-[color:var(--gold)] data-[state=active]:text-[color:var(--bg-base)] data-[state=active]:border-transparent hover:text-[color:var(--parchment)] transition font-sans-body"
              data-testid="tab-schumann"
            >
              <Waves size={14} className="mr-2" />Schumann
            </TabsTrigger>
            <TabsTrigger
              value="chart"
              className="flex-shrink-0 snap-start rounded-full border border-[color:var(--rose-gold)]/50 px-4 md:px-5 py-2 md:py-2.5 text-sm md:text-base text-[color:var(--rose-gold)] data-[state=active]:bg-[color:var(--rose-gold)] data-[state=active]:text-[color:var(--bg-base)] data-[state=active]:border-transparent hover:text-[color:var(--parchment)] transition font-sans-body md:ml-auto"
              data-testid="tab-chart"
            >
              <Sparkle size={14} className="mr-2" />Cosmic Chart
            </TabsTrigger>
            </TabsList>
          </div>

          {/* ─── TAB · TODAY & JOURNAL ─── cosmic score, brief, journal, biorhythm, meditations */}
          <TabsContent value="today" className="mt-0 space-y-10 stagger-in" data-testid="tab-content-today">
            <section data-testid="section-cosmic-score">
              <CosmicScoreDashboard />
            </section>
            <section data-testid="section-daily-brief">
              <DailyBrief brief={brief.data} isLoading={brief.isLoading} />
            </section>
            <section id="journal" className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="section-journal">
              <div className="md:col-span-2">
                <JournalSection
                  entries={journal.data || []}
                  onCreate={(v) => addEntry.mutate(v)}
                  onDelete={(id) => removeEntry.mutate(id)}
                  submitting={addEntry.isPending}
                />
              </div>
              <AlertsFeed alerts={alerts.data?.alerts || []} />
            </section>
            <section id="sky-events" data-testid="section-sky-events" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <SkyEventsToWatch />
              </div>
              <div>
                <CalendarSync />
              </div>
            </section>
            <section>
              <BiorhythmPanel />
            </section>
            <section data-testid="section-grounding">
              <MeditationLibrary />
            </section>
          </TabsContent>

          {/* ─── TAB · COSMIC WEATHER ─── solar, lunar & other influence */}
          <TabsContent value="cosmic" className="mt-0 space-y-8 stagger-in" data-testid="tab-content-cosmic">
            <div className="max-w-3xl">
              <div className="label-overline">Solar · lunar · other cosmic influence</div>
              <h2 className="mt-3 font-serif-display text-[color:var(--parchment)] text-3xl md:text-4xl tracking-tight">
                What the sky is doing right now.
              </h2>
              <p className="mt-3 font-serif-italic text-[color:var(--text-secondary)]">
                The sun, the moon, and the wider cosmos — live and translated.
              </p>
            </div>
            <div>
              <CosmicPanel current={cosmic.data} />
              <BaselineNote
                title="Reading solar activity"
                baseline="Kp 0–2 · solar wind ≈ 350–450 km/s · X-ray class A or B. That's a calm sky."
                scale={[
                  { label: "Quiet",      range: "Kp 0–2",  note: "sensitives feel steady", tone: "var(--gold)" },
                  { label: "Unsettled",  range: "Kp 3–4",  note: "restlessness, thin sleep" },
                  { label: "Geo storm",  range: "Kp 5+",   note: "headaches, big feelings", tone: "var(--rose-gold)" },
                ]}
                footnote="Data pulled live from NOAA SWPC every few minutes. Higher Kp means the Sun's charged wind is disturbing Earth's magnetic field."
              />
            </div>
            <div>
              <MoonPanel />
              <BaselineNote
                title="Reading the moon"
                baseline="0% new · 50% first/last quarter · 100% full. A full 29.5-day rhythm."
                scale={[
                  { label: "New → Waxing", range: "0–50%",   note: "plant, gather, gently build" },
                  { label: "Full",         range: "≈ 100%",  note: "peak feelings, thin sleep", tone: "var(--rose-gold)" },
                  { label: "Waning → New", range: "50–0%",   note: "release, rest, reflect" },
                ]}
                footnote="The moon's zodiac sign changes every ~2.5 days and shifts the emotional 'flavour' of each phase."
              />
            </div>
            <div>
              <AlertsFeed alerts={alerts.data?.alerts || []} />
            </div>
            <div className="md:col-span-2">
              <SkyEventsToWatch />
            </div>
          </TabsContent>

          {/* ─── TAB · SCHUMANN ─── the Earth's pulse */}
          <TabsContent value="schumann" className="mt-0 space-y-8 stagger-in" data-testid="tab-content-schumann">
            <div className="max-w-3xl">
              <div className="label-overline">The Earth&apos;s heartbeat</div>
              <h2 className="mt-3 font-serif-display text-[color:var(--parchment)] text-3xl md:text-4xl tracking-tight">
                Schumann resonance · 7.83 Hz
              </h2>
              <p className="mt-3 font-serif-italic text-[color:var(--text-secondary)]">
                Prof. W. O. Schumann predicted the cavity between Earth and the ionosphere would ring at very low frequencies. Sensitive people often feel the shifts first.
              </p>
            </div>
            <div>
              <SchumannPanel current={schumann.data} history={schumannHist.data} />
              <BaselineNote
                title="Reading the Schumann resonance"
                baseline="Fundamental frequency ≈ 7.83 Hz. Amplitude drifts through the day and week."
                scale={[
                  { label: "Quiet",       range: "0–20 pT",   note: "the everyday hum",        tone: "var(--gold)" },
                  { label: "Amplified",   range: "20–60 pT",  note: "sensitives often feel it" },
                  { label: "Spike",       range: "60+ pT",    note: "geomagnetic weather shifting", tone: "var(--rose-gold)" },
                ]}
                footnote="Data from ResonanceOne. The Schumann resonance is Earth's electromagnetic heartbeat — nothing is 'wrong' when it spikes, but many attuned people notice their sleep and mood shift with it."
              />
            </div>
            <div>
              <HistoryChart
                cosmicHistory={cosmicHist.data}
                schumannHistory={schumannHist.data}
                days={historyDays}
                onChangeDays={setHistoryDays}
              />
              <BaselineNote
                title="Reading the history chart"
                baseline="Flat lines around the middle = steady sky. Spikes = storms. Watch the correlation with your journal entries."
                footnote="Try journaling a mood word each day; over a fortnight you'll often see your energy dip on days the sun flared."
              />
            </div>
          </TabsContent>

          {/* ─── TAB · BIRTH CHART ─── prominent promo */}
          <TabsContent value="chart" className="mt-0 stagger-in" data-testid="tab-content-chart">
            <BirthChartTeaser />
          </TabsContent>
        </Tabs>

        <footer className="mt-14 md:mt-16 pt-6 pb-6 border-t border-[color:var(--gold)]/15">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[color:var(--text-secondary)]">
            <div className="font-serif-italic text-sm">
              Sources · NOAA SWPC · ResonanceOne · Swiss Ephemeris · EarthPulse narrative engine
            </div>
            <div className="flex items-center gap-5 font-sans-body text-sm">
              <Link to="/privacy" className="hover:text-[color:var(--gold)]" data-testid="footer-privacy">Privacy</Link>
              <Link to="/terms" className="hover:text-[color:var(--gold)]" data-testid="footer-terms">Terms</Link>
              <Link to="/cookies" className="hover:text-[color:var(--gold)]" data-testid="footer-cookies">Cookies</Link>
              <span className="font-serif-italic">© 2026 Earth · Pulse</span>
            </div>
          </div>
          <div className="mt-4 font-serif-italic text-[color:var(--text-muted)] text-xs text-center md:text-left">
            For reflection, not prescription. Not medical advice.
          </div>
        </footer>
      </main>
      <MobileTabBar value={activeTab} onChange={setActiveTab} />
    </div>
  );
}
